import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUpload, FaBookOpen, FaFileInvoiceDollar, FaTruck, FaUndo, FaLandmark } from 'react-icons/fa';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { BankTransactionRestDataProvider } from '../../../data/BankTransactionRestDataProvider';
import { BankAccountRestDataProvider } from '../../../data/BankAccountRestDataProvider';
import { LedgerRestDataProvider } from '../../../data/LedgerRestDataProvider';
import { FiscalYearRestDataProvider } from '../../../data/FiscalYearRestDataProvider';
import { BankTransaction } from '../../../models/BankTransaction';
import { BankAccount } from '../../../models/BankAccount';
import { Ledger } from '../../../models/Ledger';
import { FiscalYear } from '../../../models/FiscalYear';
import BankTransactionBookingModal from './BankTransactionBookingModal';
import BankTransactionPaymentModal from './BankTransactionPaymentModal';
import BankTransactionSupplierPaymentModal from './BankTransactionSupplierPaymentModal';
import BankTransactionVatPaymentModal from './BankTransactionVatPaymentModal';
import { ToolbarButtonConfig, ToolbarFilterConfig } from '../../common/Toolbar/Toolbar';
import StatsBar from '../../common/StatsBar/StatsBar';
import './BankTransactions.scss';
import '../../../styles/GenericSiteStyles.scss';

const BankTransactions: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);

  const [transactions, setTransactions]           = useState<BankTransaction[]>([]);
  const [bankAccounts, setBankAccounts]           = useState<BankAccount[]>([]);
  const [ledgers, setLedgers]                     = useState<Ledger[]>([]);
  const [fiscalYears, setFiscalYears]             = useState<FiscalYear[]>([]);
  const [selectedAccountId, setSelectedAccountId]     = useState<string>('');
  const [showUnresolvedOnly, setShowUnresolvedOnly]   = useState(false);
  const [loading, setLoading]                         = useState(false);
  const [error, setError]                         = useState<string | null>(null);
  const [initialized, setInitialized]             = useState(false);

  // ── Booking modal state ───────────────────────────────────────────────────
  const [bookingTx, setBookingTx]                 = useState<BankTransaction | null>(null);
  const [paymentTx, setPaymentTx]                 = useState<BankTransaction | null>(null);
  const [supplierPaymentTx, setSupplierPaymentTx] = useState<BankTransaction | null>(null);
  const [vatPaymentTx, setVatPaymentTx]           = useState<BankTransaction | null>(null);
  const [unbookTx, setUnbookTx]                   = useState<BankTransaction | null>(null);
  const [unbooking, setUnbooking]                 = useState(false);

  // ── Import modal state ────────────────────────────────────────────────────
  const [importOpen, setImportOpen]           = useState(false);
  const [importAccountId, setImportAccountId] = useState<string>('');
  const [importFile, setImportFile]           = useState<File | null>(null);
  const [importing, setImporting]             = useState(false);
  const fileInputRef                          = useRef<HTMLInputElement>(null);

  const txProvider      = useMemo(() => new BankTransactionRestDataProvider(), []);
  const accountProvider = useMemo(() => new BankAccountRestDataProvider(), []);
  const ledgerProvider  = useMemo(() => new LedgerRestDataProvider(), []);
  const fyProvider      = useMemo(() => new FiscalYearRestDataProvider(), []);

  // Load reference data once on mount, then signal ready
  useEffect(() => {
    Promise.all([
      accountProvider.getAll(),
      ledgerProvider.getAll(),
      fyProvider.getAll(),
    ])
      .then(([accounts, fetchedLedgers, fetchedFy]) => {
        setBankAccounts(accounts);
        setLedgers(fetchedLedgers);
        // Sort fiscal years newest-first so auto-select picks the latest
        setFiscalYears([...fetchedFy].sort((a, b) => b.start.localeCompare(a.start)));
        setInitialized(true);
      })
      .catch(e => setError(e.message));
  }, [accountProvider, ledgerProvider, fyProvider]);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await txProvider.getAll();
      setTransactions(
        selectedAccountId ? all.filter(tx => tx.bankAccountId === selectedAccountId) : all
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [txProvider, selectedAccountId]);

  // Reload whenever filters change, but only after reference data is ready
  useEffect(() => {
    if (!initialized) return;
    loadTransactions();
  }, [loadTransactions, initialized]);

  const getSignedAmount = useCallback((tx: BankTransaction): number => {
    const isPositive = tx.creditDebitIndicator === 'C' || tx.creditDebitIndicator === 'RD';
    return isPositive ? tx.amount : -tx.amount;
  }, []);

  const getBankAccountName = useCallback((tx: BankTransaction): string => {
    if (!tx.bankAccountId) return '-';
    return bankAccounts.find(a => a.id === tx.bankAccountId)?.name ?? '-';
  }, [bankAccounts]);

  const handleOpenImport = useCallback(() => {
    setImportAccountId(selectedAccountId);
    setImportFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setImportOpen(true);
  }, [selectedAccountId]);

  const handleCloseImport = () => {
    if (importing) return;
    setImportOpen(false);
    setImportFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImport = async () => {
    if (!importFile || !importAccountId) return;
    setImporting(true);
    try {
      await txProvider.importMt940(importAccountId, importFile);
      handleCloseImport();
      await loadTransactions();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setImporting(false);
    }
  };

  const handleBooked = useCallback((updatedTx: BankTransaction) => {
    setTransactions(prev => prev.map(t => t.id === updatedTx.id ? updatedTx : t));
    setBookingTx(null);
  }, []);


  const handleMatched = useCallback((updatedTx: BankTransaction) => {
    setTransactions(prev => prev.map(t => t.id === updatedTx.id ? updatedTx : t));
    setPaymentTx(null);
  }, []);

  const handleSupplierMatched = useCallback((updatedTx: BankTransaction) => {
    setTransactions(prev => prev.map(t => t.id === updatedTx.id ? updatedTx : t));
    setSupplierPaymentTx(null);
  }, []);

  const handleVatPaymentBooked = useCallback((updatedTx: BankTransaction) => {
    setTransactions(prev => prev.map(t => t.id === updatedTx.id ? updatedTx : t));
    setVatPaymentTx(null);
  }, []);

  const handleUnbook = useCallback(async () => {
    if (!unbookTx) return;
    setUnbooking(true);
    try {
      await txProvider.unbook(unbookTx.id);
      const cleared: BankTransaction = {
        ...unbookTx,
        ledgerReferenceCode: null,
        isPaymentMatched: false,
        isSupplierPaymentMatched: false,
        isVatPaymentBooked: false,
      };
      setTransactions(prev => prev.map(t => t.id === cleared.id ? cleared : t));
      setUnbookTx(null);
    } catch {
      // toast handled by BaseRestDataProvider
    } finally {
      setUnbooking(false);
    }
  }, [unbookTx, txProvider]);

  // Summary figures derived from the current filtered set
  const totalIncoming = useMemo(
    () => transactions
      .filter(tx => tx.creditDebitIndicator === 'C' || tx.creditDebitIndicator === 'RD')
      .reduce((sum, tx) => sum + tx.amount, 0),
    [transactions]
  );
  const totalOutgoing = useMemo(
    () => transactions
      .filter(tx => tx.creditDebitIndicator === 'D' || tx.creditDebitIndicator === 'RC')
      .reduce((sum, tx) => sum + tx.amount, 0),
    [transactions]
  );
  const netBalance = totalIncoming - totalOutgoing;

  // Apply unresolved-only filter on top of the account-filtered set
  const displayedTransactions = useMemo(
    () => showUnresolvedOnly
      ? transactions.filter(tx => !tx.ledgerReferenceCode)
      : transactions,
    [transactions, showUnresolvedOnly]
  );

  const columns: DetailedListColumn<BankTransaction>[] = useMemo(() => [
    {
      key: 'valueDate',
      header: t('screens:bankTransactions.columns.date'),
      width: 120,
      render: tx => tx.valueDate,
    },
    {
      key: 'bankAccount',
      header: t('screens:bankTransactions.columns.bankAccount'),
      width: 200,
      render: tx => getBankAccountName(tx),
    },
    {
      key: 'counterpartyIban',
      header: t('screens:bankTransactions.columns.counterpartyIban'),
      width: 200,
      render: tx => tx.narrativeCounterpartyIban || '-',
    },
    {
      key: 'counterpartyName',
      header: t('screens:bankTransactions.columns.counterpartyName'),
      width: 200,
      render: tx => tx.narrativeCounterpartyName || '-',
      type: 'string',
      filterValue: tx => tx.narrativeCounterpartyName ?? '',
    },
    {
      key: 'remittanceInfo',
      header: t('screens:bankTransactions.columns.remittanceInfo'),
      render: tx => tx.narrativeRemittanceInfo || tx.narrative || tx.customerReference || '-',
      type: 'string',
      filterValue: tx => tx.narrativeRemittanceInfo || tx.narrative || tx.customerReference || '',
    },
    {
      key: 'ledgerReferenceCode',
      header: t('screens:bankTransactions.columns.ledgerRef'),
      width: 160,
      render: tx => tx.ledgerReferenceCode
        ? <span className="bank-transactions-booked-ref">{tx.ledgerReferenceCode}</span>
        : <span className="bank-transactions-unbooked">—</span>,
    },
    {
      key: 'amount',
      header: t('screens:bankTransactions.columns.amount'),
      width: 140,
      align: 'right',
      render: tx => {
        const signed = getSignedAmount(tx);
        return (
          <span className={`bank-transactions-amount ${signed >= 0 ? 'bank-transactions-amount--positive' : 'bank-transactions-amount--negative'}`}>
            {signed >= 0 ? '+' : ''}
            {signed.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      },
    },
  ], [t, getBankAccountName, getSignedAmount]);

  const rowActions = useCallback((tx: BankTransaction) => {
    // A transaction is "processed" once it has been booked or invoice-matched in any way.
    // Once processed, only the Unbook action is relevant.
    const isProcessed = !!tx.ledgerReferenceCode || tx.isPaymentMatched || tx.isSupplierPaymentMatched || tx.isVatPaymentBooked;

    return (
      <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager}`}>
        {!isProcessed && (
          <>
            {/* Manual ledger booking */}
            <button
              type="button"
              className="dl-action-btn dl-action-btn--primary"
              title={t('screens:bankTransactions.booking.book')}
              onClick={() => setBookingTx(tx)}
            >
              <FaBookOpen />
            </button>
            {/* Match customer invoice */}
            <button
              type="button"
              className="dl-action-btn dl-action-btn--info"
              title={t('screens:bankTransactions.invoiceType.matchCustomer')}
              onClick={() => setPaymentTx(tx)}
            >
              <FaFileInvoiceDollar />
            </button>
            {/* Match supplier invoice */}
            <button
              type="button"
              className="dl-action-btn dl-action-btn--warning"
              title={t('screens:bankTransactions.invoiceType.matchSupplier')}
              onClick={() => setSupplierPaymentTx(tx)}
            >
              <FaTruck />
            </button>
            {/* Book Belastingdienst VAT payment / refund */}
            <button
              type="button"
              className="dl-action-btn dl-action-btn--info"
              title={t('screens:bankTransactions.vatPayment.buttonTitle')}
              onClick={() => setVatPaymentTx(tx)}
            >
              <FaLandmark />
            </button>
          </>
        )}
        {tx.ledgerReferenceCode && (
          <button
            type="button"
            className="dl-action-btn dl-action-btn--danger"
            title={t('screens:bankTransactions.unbook.button', { ref: tx.ledgerReferenceCode })}
            onClick={() => setUnbookTx(tx)}
          >
            <FaUndo />
          </button>
        )}
      </RoleGuard>
    );
  }, [t]);

  // ── Toolbar configs ──────────────────────────────────────────────────────────
  const toolbarFilters = useMemo<ToolbarFilterConfig[]>(() => [
    {
      key: 'bankAccount',
      type: 'select',
      label: t('screens:bankTransactions.fields.bankAccount'),
      value: selectedAccountId,
      onChange: setSelectedAccountId,
      allLabel: t('screens:bankTransactions.allAccounts'),
      options: bankAccounts.map(a => ({ label: a.name, value: a.id })),
    },
    {
      key: 'unresolvedOnly',
      type: 'checkbox',
      label: t('screens:bankTransactions.unresolvedOnly'),
      value: showUnresolvedOnly,
      onChange: setShowUnresolvedOnly,
    },
  ], [bankAccounts, selectedAccountId, showUnresolvedOnly, t]);

  const toolbarButtons = useMemo<ToolbarButtonConfig[]>(() => [
    {
      key: '__importMt940',
      icon: <FaUpload />,
      label: t('screens:bankTransactions.import.button'),
      onClick: handleOpenImport,
      variant: 'primary',
      role: `${UserRoles.Admin},${UserRoles.Manager}`,
    },
  ], [t, handleOpenImport]);

  return (
    <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`} redirectOnDeny>
      <div className="bank-transactions-page">
        <MainScreenHeader title={t('screens:bankTransactions.title')} />
        {error && <ErrorMessage message={error} />}

        {/* ── Summary stats strip ── */}
        <StatsBar flush items={[
          { key: 'incoming', label: t('screens:bankTransactions.summary.incoming'), value: `€ ${totalIncoming.toFixed(2)}`, variant: 'positive' },
          { key: 'outgoing', label: t('screens:bankTransactions.summary.outgoing'), value: `€ ${totalOutgoing.toFixed(2)}`, variant: 'negative' },
          { key: 'balance',  label: t('screens:bankTransactions.summary.balance'),  value: `€ ${netBalance.toFixed(2)}`,   variant: netBalance >= 0 ? 'positive' : 'negative' },
        ]} />

        <DetailedListComponent
          dataSource={{ data: displayedTransactions, setData: setTransactions }}
          columns={columns}
          rowKey={tx => tx.id}
          actions={rowActions}
          editRole={UserRoles.Manager}
          addRole=""
          deleteRole={UserRoles.Admin}
          disableAdd
          disableDelete
          disableEdit
          actionsColumnWidth={260}
          actionsColumnFixed
          toolbarFilters={toolbarFilters}
          toolbarButtons={toolbarButtons}
        />

        {loading && <div className="bank-transactions-loading">{t('common:loading')}</div>}

        {/* ── Booking modal ── */}
        {bookingTx && (
          <BankTransactionBookingModal
            transaction={bookingTx}
            bankAccounts={bankAccounts}
            ledgers={ledgers}
            fiscalYears={fiscalYears}
            onClose={() => setBookingTx(null)}
            onBooked={handleBooked}
          />
        )}

        {/* ── Payment matching modal (customer invoices) ── */}
        {paymentTx && (
          <BankTransactionPaymentModal
            transaction={paymentTx}
            onClose={() => setPaymentTx(null)}
            onMatched={handleMatched}
          />
        )}

        {/* ── Supplier payment matching modal ── */}
        {supplierPaymentTx && (
          <BankTransactionSupplierPaymentModal
            transaction={supplierPaymentTx}
            ledgers={ledgers}
            fiscalYears={fiscalYears}
            onClose={() => setSupplierPaymentTx(null)}
            onMatched={handleSupplierMatched}
          />
        )}

        {/* ── Belastingdienst VAT payment / refund modal ── */}
        {vatPaymentTx && (
          <BankTransactionVatPaymentModal
            transaction={vatPaymentTx}
            ledgers={ledgers}
            fiscalYears={fiscalYears}
            onClose={() => setVatPaymentTx(null)}
            onBooked={handleVatPaymentBooked}
          />
        )}

        {/* ── Unbook confirmation dialog ── */}
        {unbookTx && (
          <PopupDialog
            open
            title={t('screens:bankTransactions.unbook.title')}
            onClose={() => !unbooking && setUnbookTx(null)}
            actions={
              <>
                <button
                  type="button"
                  className="btn btn-danger me-2"
                  disabled={unbooking}
                  onClick={handleUnbook}
                >
                  <FaUndo style={{ marginRight: 6 }} />
                  {unbooking
                    ? t('screens:bankTransactions.unbook.undoing')
                    : t('screens:bankTransactions.unbook.confirm')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={unbooking}
                  onClick={() => setUnbookTx(null)}
                >
                  {t('common:cancel')}
                </button>
              </>
            }
          >
            <p>{t('screens:bankTransactions.unbook.message', { ref: unbookTx.ledgerReferenceCode })}</p>
            {(unbookTx.isPaymentMatched || unbookTx.isSupplierPaymentMatched) && (
              <p className="text-warning small">
                {t('screens:bankTransactions.unbook.invoiceWarning')}
              </p>
            )}
            {unbookTx.isVatPaymentBooked && (
              <p className="text-warning small">
                {t('screens:bankTransactions.unbook.vatPaymentWarning')}
              </p>
            )}
          </PopupDialog>
        )}

        {/* ── Import MT-940 modal ── */}
        <PopupDialog
          open={importOpen}
          title={t('screens:bankTransactions.import.title')}
          onClose={handleCloseImport}
          actions={
            <>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!importFile || !importAccountId || importing}
                onClick={handleImport}
              >
                <FaUpload style={{ marginRight: 6 }} />
                {importing
                  ? t('screens:bankTransactions.import.importing')
                  : t('screens:bankTransactions.import.importButton')}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={importing}
                onClick={handleCloseImport}
              >
                {t('common:cancel')}
              </button>
            </>
          }
        >
          <div className="bank-transactions-import-form">
            <div className="mb-3">
              <label className="form-label">{t('screens:bankTransactions.import.selectAccount')}</label>
              <select
                className="form-select"
                value={importAccountId}
                onChange={e => setImportAccountId(e.target.value)}
                disabled={importing}
              >
                <option value="">{t('screens:bankTransactions.import.accountPlaceholder')}</option>
                {bankAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}{a.ibanAccountNumber ? ` — ${a.ibanAccountNumber}` : ''}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">{t('screens:bankTransactions.import.selectFile')}</label>
              <input
                ref={fileInputRef}
                type="file"
                className="form-control"
                accept=".sta,.mt940,.940,.txt"
                disabled={importing}
                onChange={e => setImportFile(e.target.files?.[0] ?? null)}
              />
              <div className="form-text">{t('screens:bankTransactions.import.fileHint')}</div>
            </div>
          </div>
        </PopupDialog>
      </div>
    </RoleGuard>
  );
};

export default BankTransactions;
